import React, { useState, useEffect, useRef } from 'react';
import { Search, ScrollText, X, LogOut, UserCircle, Settings } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import LoginDialog from './auth/LoginDialog';
import RegisterDialog from './auth/RegisterDialog';

interface HeaderProps {
  searchQuery?: string;
  onSearchChange?: (query: string) => void;
}

const CUSTOMER_URL = process.env.REACT_APP_CUSTOMER_URL || '';

function getCustomerUrlWithUtm(): string {
  if (!CUSTOMER_URL) return '';
  const url = new URL(CUSTOMER_URL);
  const utm = sessionStorage.getItem('utm_source');
  if (utm && utm !== 'direct') {
    url.searchParams.set('utm_source', utm);
  }
  return url.toString();
}

const Header: React.FC<HeaderProps> = ({ searchQuery: externalQuery, onSearchChange }) => {
  const [localQuery, setLocalQuery] = useState('');
  const [showMobileSearch, setShowMobileSearch] = useState(false);
  const [loginOpen, setLoginOpen] = useState(false);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const navigate = useNavigate();
  const { user, loading, logout } = useAuth();
  const { t } = useTranslation('nav');
  const userMenuRef = useRef<HTMLDivElement>(null);

  const searchQuery = externalQuery ?? localQuery;
  const setSearchQuery = onSearchChange ?? setLocalQuery;

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setUserMenuOpen(false);
      }
    };

    if (userMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [userMenuOpen]);

  const handleLogout = async () => {
    try {
      setUserMenuOpen(false);
      await logout();
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleSwitchToRegister = () => {
    setLoginOpen(false);
    setRegisterOpen(true);
  };

  const handleSwitchToLogin = () => {
    setRegisterOpen(false);
    setLoginOpen(true);
  };

  const getUserDisplayName = () => {
    return user?.name || user?.email?.split('@')[0] || 'User';
  };

  return (
    <>
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center justify-between h-16">
            {/* Logo (mascot + wordmark stacked side-by-side; clicking either part lands on the customer site) */}
            <div className="flex-shrink-0 flex items-center gap-2 cursor-pointer" onClick={() => {
              if (CUSTOMER_URL) {
                window.location.href = getCustomerUrlWithUtm();
              } else {
                navigate('/');
              }
            }}>
              <img
                src="/logo.png"
                alt="E-Mercado Hub"
                className="h-10 w-auto"
              />
              <img
                src="/logo-text-green.png"
                alt=""
                aria-hidden
                className="h-3 w-auto hidden sm:block"
              />
            </div>

            {/* Search Bar */}
            <div className="flex-1 max-w-2xl mx-8">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder={t('search_placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-full leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                />
              </div>
            </div>

            {/* Right Side */}
            <div className="flex items-center space-x-4">
              <button
                className="flex flex-col items-center p-2 text-gray-600 hover:text-primary transition-colors"
                onClick={() => navigate('/transactions')}
              >
                <ScrollText className="h-6 w-6" />
                <span className="text-xs mt-0.5">{t('transactions')}</span>
              </button>

              {loading && !user ? (
                // Skeleton while Firebase resolves the session — covers the cold-mount
                // gap, the cross-app ?auth= handoff, and the shared-cookie path so the
                // Login/Register buttons never flash before the user state is known.
                <div className="flex items-center space-x-3 py-2">
                  <div className="h-8 w-8 rounded-full bg-gray-200 animate-pulse" />
                  <div className="h-4 w-20 rounded bg-gray-200 animate-pulse" />
                </div>
              ) : user ? (
                <>
                  {/* User Menu with Dropdown */}
                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="flex items-center space-x-2 p-2 text-gray-600 hover:text-primary transition-colors"
                    >
                      {user.photoURL ? (
                        <img
                          src={user.photoURL}
                          alt="Profile"
                          className="h-8 w-8 rounded-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <UserCircle className="h-8 w-8" />
                      )}
                      <span className="text-sm font-medium">{getUserDisplayName()}</span>
                    </button>

                    {/* Dropdown — Settings always shown; Logout hidden for
                        Mosan WebView users since the WebView can't host
                        the email/Google sign-in form needed to come back. */}
                    {userMenuOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50 border border-gray-200">
                        <button
                          onMouseDown={(e) => { e.stopPropagation(); setUserMenuOpen(false); navigate('/settings'); }}
                          className="w-full flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-50"
                        >
                          <Settings className="h-4 w-4" />
                          <span>{t('settings')}</span>
                        </button>
                        {user?.authSource !== 'mosan' && (
                          <button
                            onMouseDown={(e) => { e.stopPropagation(); handleLogout(); }}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-gray-50"
                          >
                            <LogOut className="h-4 w-4" />
                            <span>{t('logout')}</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setLoginOpen(true)}
                    className="px-4 py-2 text-sm font-medium text-primary border-2 border-primary rounded-lg hover:bg-primary hover:text-white transition-colors"
                  >
                    {t('login')}
                  </button>
                  <button
                    onClick={() => setRegisterOpen(true)}
                    className="px-4 py-2 text-sm font-medium text-white bg-primary border-2 border-primary rounded-lg hover:brightness-90 transition-colors"
                  >
                    {t('register')}
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Mobile Navigation */}
          <div className="md:hidden flex items-center justify-between h-16">
            {/* Logo (mobile — mascot only, wordmark hidden to keep header tight) */}
            <div className="flex-shrink-0 cursor-pointer" onClick={() => {
              if (CUSTOMER_URL) {
                window.location.href = getCustomerUrlWithUtm();
              } else {
                navigate('/');
              }
            }}>
              <img
                src="/logo.png"
                alt="E-Mercado Hub"
                className="h-10 w-auto"
              />
            </div>

            {/* Right Icons */}
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setShowMobileSearch(!showMobileSearch)}
                className="p-2 text-gray-600 hover:text-primary transition-colors"
              >
                {showMobileSearch ? <X className="h-6 w-6" /> : <Search className="h-6 w-6" />}
              </button>
              <button
                className="p-2 text-gray-600 hover:text-primary transition-colors"
                onClick={() => navigate('/transactions')}
              >
                <ScrollText className="h-6 w-6" />
              </button>

              {loading && !user ? (
                <div className="p-2">
                  <div className="h-7 w-7 rounded-full bg-gray-200 animate-pulse" />
                </div>
              ) : user ? (
                <>
                  {/* Mobile User Menu */}
                  <div className="relative" ref={userMenuRef}>
                    <button
                      onClick={() => setUserMenuOpen(!userMenuOpen)}
                      className="p-2 text-gray-600 hover:text-primary transition-colors"
                    >
                      {user.photoURL ? (
                        <img
                          src={user.photoURL}
                          alt="Profile"
                          className="h-7 w-7 rounded-full object-cover"
                          referrerPolicy="no-referrer"
                        />
                      ) : (
                        <UserCircle className="h-6 w-6" />
                      )}
                    </button>

                    {userMenuOpen && (
                      <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 z-50 border border-gray-200">
                        <div className="px-4 py-2 border-b border-gray-100">
                          <p className="text-sm font-medium text-gray-900 truncate">{getUserDisplayName()}</p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                        <button
                          onClick={() => { setUserMenuOpen(false); navigate('/settings'); }}
                          className="w-full flex items-center space-x-2 px-4 py-2 text-gray-700 hover:bg-gray-50"
                        >
                          <Settings className="h-4 w-4" />
                          <span>{t('settings')}</span>
                        </button>
                        {user?.authSource !== 'mosan' && (
                          <button
                            onClick={handleLogout}
                            className="w-full flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-gray-50"
                          >
                            <LogOut className="h-4 w-4" />
                            <span>{t('logout')}</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <button
                  onClick={() => setLoginOpen(true)}
                  className="px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-lg hover:brightness-90 transition-colors"
                >
                  {t('login')}
                </button>
              )}
            </div>
          </div>

          {/* Mobile Search Bar */}
          {showMobileSearch && (
            <div className="md:hidden py-3 border-t border-gray-200">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder={t('search_placeholder')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  autoFocus
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-full leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary text-sm"
                />
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Auth Dialogs */}
      <LoginDialog
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onSwitchToRegister={handleSwitchToRegister}
      />
      <RegisterDialog
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onSwitchToLogin={handleSwitchToLogin}
      />
    </>
  );
};

export default Header;
